import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/Nav";
import { getToken } from "../lib/auth";
import { track } from "../lib/analytics";

// MVP reveal — clean, emotional, simple. One short fade-in. No export,
// no share, no chapter recap. Focus is on: "You chose well."
export default function WinnerReveal() {
  const [winner,   setWinner]   = useState(null);
  const [runnerUp, setRunnerUp] = useState(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShown(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => { track('reveal_reached'); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch persisted reveal result from backend
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch('/api/reveal', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.winner)    setWinner(data.winner);
        if (data?.runner_up) setRunnerUp(data.runner_up);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!winner || !runnerUp) {
    return (
      <AppShell>
        <div className="min-h-[calc(100vh-80px)] flex items-center justify-center">
          <div className="text-center glass rounded-3xl p-10 max-w-sm">
            <div className="font-display text-2xl mb-3">Your reveal isn't ready yet</div>
            <p className="text-white/50 text-sm">Complete your bracket rounds to unlock your Bora Moyo.</p>
            <Link to="/bracket" className="mt-6 inline-flex rounded-full bg-gradient-gold text-[#080B14] font-semibold px-6 py-3 text-sm">
              Continue journey
            </Link>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="relative min-h-[calc(100vh-80px)] px-6 md:px-12 py-10 md:py-16 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none bg-gradient-hero" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[520px] w-[520px] rounded-full bg-[#D4AF37] opacity-[0.09] blur-[120px]" />

        <div
          className={`relative mx-auto max-w-5xl transition-all duration-500 ease-out ${
            shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
          }`}
        >
          {/* Heading */}
          <div className="text-center">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#F2DE94] mb-5">
              Only one remains
            </div>
            <h1 className="font-display text-5xl md:text-7xl leading-[1.02]">
              Your <span className="italic text-gradient-gold">Bora Moyo</span>
              <br />
              <span className="text-white/85 text-4xl md:text-6xl">
                has been selected.
              </span>
            </h1>
          </div>

          {/* 2 → 1 visual — static, no phased cinematic */}
          <div className="mt-12 grid grid-cols-[1fr_auto_1.15fr] md:grid-cols-[0.9fr_auto_1.2fr] gap-3 md:gap-6 items-center" data-testid="reveal-stage">
            {/* Runner-up */}
            <div
              data-testid="runner-up"
              className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 opacity-40 scale-[0.96]"
            >
              <div className="relative aspect-[4/5]">
                <img
                  src={runnerUp.image}
                  alt={runnerUp.name}
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{ filter: "grayscale(90%) blur(1.5px)" }}
                />
                <div className="absolute inset-0 bg-[#05070d]/60" />
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">
                    Finalist · path closed
                  </div>
                  <div className="font-display text-xl text-white/60 mt-1">
                    {runnerUp.name}
                  </div>
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex flex-col items-center gap-2 px-2 md:px-4">
              <span className="h-2 w-2 rounded-full bg-white/30" />
              <span className="h-12 w-px bg-gradient-to-b from-white/25 via-[rgba(212,175,55,0.5)] to-[#D4AF37]" />
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-[#D4AF37]">
                <path d="M12 4v14m0 0-5-5m5 5 5-5" />
              </svg>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] whitespace-nowrap">
                2 → 1
              </div>
            </div>

            {/* Winner */}
            <div
              data-testid="winner-card"
              className="relative rounded-3xl overflow-hidden ring-gold glow-gold"
            >
              <div className="relative aspect-[4/5]">
                <img
                  src={winner.image}
                  alt={winner.name}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-[#05070d]/25 to-transparent" />
                <div className="absolute top-5 left-5">
                  <span className="glass rounded-full px-3.5 py-1.5 text-[10px] uppercase tracking-[0.3em] text-[#F2DE94]">
                    ★ Your Bora Moyo
                  </span>
                </div>
                <div className="absolute bottom-7 left-7 right-7">
                  <div className="font-display text-5xl md:text-6xl leading-none">
                    {winner.name}
                  </div>
                  <div className="text-white/65 mt-2 text-sm md:text-base">
                    {winner.occupation} · {winner.city}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quiet affirmation */}
          <div className="mt-10 text-center" data-testid="affirmation">
            <div className="font-display italic text-2xl md:text-3xl text-gradient-gold">
              You chose well.
            </div>
          </div>

          {/* Letter + CTAs */}
          <div className="mt-10 grid md:grid-cols-[1.1fr_1fr] gap-8 items-start">
            <div className="glass-strong rounded-3xl p-7">
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-3">
                A letter, from the journey
              </div>
              <p className="font-display italic text-xl leading-snug text-white/85">
                "{winner.prompts[0]?.a}"
              </p>
              <div className="hairline my-5" />
              <div className="grid grid-cols-3 gap-3 text-center">
                {[
                  ["Overall", `${winner.compatibility}%`, "gold"],
                  ["Values", winner.scores.values, "forest"],
                  ["Chemistry", winner.scores.chemistry, "burgundy"],
                ].map(([k, v, tone]) => (
                  <div key={k} className="rounded-2xl bg-white/[0.03] p-3">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">
                      {k}
                    </div>
                    <div
                      className={`font-display text-2xl ${tone === "gold" ? "text-gradient-gold" : tone === "forest" ? "text-[#8FD6A6]" : "text-[#F5A8B5]"}`}
                    >
                      {v}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <Link
                to="/chat"
                data-testid="start-chat"
                className="rounded-full bg-gradient-gold text-[#080B14] font-semibold px-6 py-4 glow-gold text-center"
              >
                Start a conversation
              </Link>
              <Link
                to="/chat"
                data-testid="plan-date"
                className="rounded-full border border-white/15 hover:border-white/35 px-6 py-4 text-center text-white/85"
              >
                Plan a first meeting
              </Link>
              <Link
                to="/bracket"
                data-testid="revisit-bracket"
                className="text-xs text-white/55 hover:text-white underline underline-offset-4 text-center mt-1"
              >
                Revisit the bracket
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
